// src/components/CounselorIntensiveControl.js
// Counselor control panel for Pre-Marriage Intensive modules
// Allows locking/unlocking modules, revealing results, and previewing content

import React, { useState } from 'react';
import { 
  Lock, 
  Unlock, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Heart,
  Users,
  Award,
  FileQuestion,
  Zap
} from 'lucide-react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { INTENSIVE_MODULE_DATA, INTENSIVE_MODULE_NAMES } from '../data/intensiveModules';
import IntensiveTeachingPresentation from './IntensiveTeachingPresentation';
import IntensivePrayerPresentation from './IntensivePrayerPresentation';
import IntensiveQuizPreview from './IntensiveQuizPreview';

const CounselorIntensiveControl = ({ 
  couple, 
  coupleDocId, 
  partner1Progress, 
  partner2Progress, 
  onUpdate 
}) => {
  const [expandedModule, setExpandedModule] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showTeaching, setShowTeaching] = useState(false);
  const [showPrayer, setShowPrayer] = useState(false);
  const [showQuizPreview, setShowQuizPreview] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [prayerMode, setPrayerMode] = useState('long'); // 'long' or 'short'

  // Get the correct couple ID
  const effectiveCoupleId = coupleDocId || couple?.id || couple?.docId;

  // Get permissions from couple document
  const intensivePermissions = couple?.intensivePermissions || {};

  // Get module status
  const getModuleStatus = (moduleNumber) => {
    const permission = intensivePermissions[moduleNumber] || {};
    
    // Partner completion status
    const p1Completed = partner1Progress?.completedIntensiveModules?.includes(moduleNumber) || false;
    const p2Completed = partner2Progress?.completedIntensiveModules?.includes(moduleNumber) || false;
    
    // Partner scores
    const p1Score = partner1Progress?.intensiveModuleScores?.[moduleNumber];
    const p2Score = partner2Progress?.intensiveModuleScores?.[moduleNumber];
    
    // Module data
    const moduleData = INTENSIVE_MODULE_DATA[moduleNumber];
    const maxScore = moduleData?.questions?.length * 5 || 80;

    return {
      canTake: permission.canTake || false,
      resultsRevealed: permission.resultsRevealed || false,
      unlockedAt: permission.unlockedAt,
      revealedAt: permission.revealedAt,
      partner1Completed: p1Completed,
      partner2Completed: p2Completed,
      bothCompleted: p1Completed && p2Completed,
      partner1Score: p1Score,
      partner2Score: p2Score,
      maxScore
    };
  };

  // Unlock a module
  const handleUnlockModule = async (moduleNumber) => {
    if (!effectiveCoupleId) {
      alert('Error: Could not find couple ID');
      return;
    }

    setProcessing(true);
    try {
      const coupleRef = doc(db, 'couples', effectiveCoupleId);
      await updateDoc(coupleRef, {
        [`intensivePermissions.${moduleNumber}.canTake`]: true,
        [`intensivePermissions.${moduleNumber}.unlockedAt`]: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ Module ${moduleNumber} unlocked`);
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error unlocking module:', error);
      alert('Error unlocking module: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Lock a module
  const handleLockModule = async (moduleNumber) => {
    if (!effectiveCoupleId) {
      alert('Error: Could not find couple ID');
      return;
    }

    setProcessing(true);
    try {
      const coupleRef = doc(db, 'couples', effectiveCoupleId);
      await updateDoc(coupleRef, {
        [`intensivePermissions.${moduleNumber}.canTake`]: false,
        updatedAt: serverTimestamp()
      });
      
      console.log(`🔒 Module ${moduleNumber} locked`);
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error locking module:', error);
      alert('Error locking module: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Reveal results for a module
  const handleRevealResults = async (moduleNumber) => {
    if (!effectiveCoupleId) {
      alert('Error: Could not find couple ID');
      return;
    }

    setProcessing(true);
    try {
      const coupleRef = doc(db, 'couples', effectiveCoupleId);
      await updateDoc(coupleRef, {
        [`intensivePermissions.${moduleNumber}.resultsRevealed`]: true,
        [`intensivePermissions.${moduleNumber}.revealedAt`]: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log(`👁️ Module ${moduleNumber} results revealed`);
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error revealing results:', error);
      alert('Error revealing results: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Hide results for a module
  const handleHideResults = async (moduleNumber) => {
    if (!effectiveCoupleId) {
      alert('Error: Could not find couple ID');
      return;
    }

    setProcessing(true);
    try {
      const coupleRef = doc(db, 'couples', effectiveCoupleId);
      await updateDoc(coupleRef, {
        [`intensivePermissions.${moduleNumber}.resultsRevealed`]: false,
        [`intensivePermissions.${moduleNumber}.revealedAt`]: null,
        updatedAt: serverTimestamp()
      });
      
      console.log(`🙈 Module ${moduleNumber} results hidden`);
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error hiding results:', error);
      alert('Error hiding results: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Unlock all modules at once
  const handleUnlockAll = async () => {
    if (!effectiveCoupleId) {
      alert('Error: Could not find couple ID');
      return;
    }

    setProcessing(true);
    try {
      const coupleRef = doc(db, 'couples', effectiveCoupleId);
      const updates = {};
      
      for (let i = 1; i <= 10; i++) {
        updates[`intensivePermissions.${i}.canTake`] = true;
        updates[`intensivePermissions.${i}.unlockedAt`] = serverTimestamp();
      }
      updates.updatedAt = serverTimestamp();
      
      await updateDoc(coupleRef, updates);
      
      console.log('✅ All modules unlocked');
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error unlocking all modules:', error);
      alert('Error unlocking all modules: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Get score level for display
  const getScoreLevel = (score, moduleNumber) => {
    const module = INTENSIVE_MODULE_DATA[moduleNumber];
    if (!module?.scoringGuidance) return null;
    
    const { excellent, developing, vulnerable } = module.scoringGuidance;
    
    if (score >= excellent.range[0]) {
      return { level: excellent.level, color: 'green', icon: '✅' };
    } else if (score >= developing.range[0]) {
      return { level: developing.level, color: 'yellow', icon: '⚠️' };
    } else {
      return { level: vulnerable.level, color: 'red', icon: '🔴' };
    }
  };

  // Open prayer with specific mode
  const handleOpenPrayer = (moduleNumber, mode) => {
    setSelectedModule(moduleNumber);
    setPrayerMode(mode);
    setShowPrayer(true);
  };

  // Calculate overall progress
  const getOverallProgress = () => {
    let unlockedCount = 0;
    let p1CompletedCount = 0;
    let p2CompletedCount = 0;

    for (let i = 1; i <= 10; i++) {
      if (intensivePermissions[i]?.canTake) unlockedCount++;
      if (partner1Progress?.completedIntensiveModules?.includes(i)) p1CompletedCount++;
      if (partner2Progress?.completedIntensiveModules?.includes(i)) p2CompletedCount++;
    }

    return { unlockedCount, p1CompletedCount, p2CompletedCount };
  };

  const progress = getOverallProgress();

  return (
    <>
      {/* Teaching Slideshow Modal */}
      {showTeaching && selectedModule && (
        <IntensiveTeachingPresentation
          moduleNumber={selectedModule}
          onClose={() => setShowTeaching(false)}
        />
      )}

      {/* Prayer Slideshow Modal */}
      {showPrayer && selectedModule && (
        <IntensivePrayerPresentation
          moduleNumber={selectedModule}
          initialMode={prayerMode}
          onClose={() => setShowPrayer(false)}
        />
      )}

      {/* Quiz Preview Modal */}
      {showQuizPreview && selectedModule && (
        <IntensiveQuizPreview
          moduleNumber={selectedModule}
          onClose={() => setShowQuizPreview(false)}
        />
      )}

      <div className="space-y-6">
        {/* Header with Summary */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <Award className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Pre-Marriage Intensive</h2>
                <p className="text-emerald-100">10 Modules • 160 Questions</p>
              </div>
            </div>
            
            <button
              onClick={handleUnlockAll}
              disabled={processing}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition disabled:opacity-50"
            >
              <Unlock className="w-5 h-5" />
              Unlock All Modules
            </button>
          </div>

          {/* Progress Summary */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 rounded-lg p-3">
              <div className="text-2xl font-bold">{progress.unlockedCount}/10</div>
              <div className="text-sm text-emerald-200">Modules Unlocked</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <div className="text-2xl font-bold">{progress.p1CompletedCount}/10</div>
              <div className="text-sm text-emerald-200">{couple?.partner1Name || 'Partner 1'}</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <div className="text-2xl font-bold">{progress.p2CompletedCount}/10</div>
              <div className="text-sm text-emerald-200">{couple?.partner2Name || 'Partner 2'}</div>
            </div>
          </div>
        </div>

        {/* Module Management */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Module Management</h3>
              <span className="text-sm text-gray-500">Click to expand details</span>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((moduleNumber) => {
              const module = INTENSIVE_MODULE_DATA[moduleNumber];
              const status = getModuleStatus(moduleNumber);
              const isExpanded = expandedModule === moduleNumber;

              return (
                <div key={moduleNumber} className="bg-white">
                  {/* Module Header Row */}
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition"
                    onClick={() => setExpandedModule(isExpanded ? null : moduleNumber)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${
                        status.canTake ? 'bg-emerald-100' : 'bg-gray-100'
                      }`}>
                        {module?.icon || '📖'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">Module {moduleNumber}</span>
                          {status.canTake ? (
                            <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                              🔓 Unlocked
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                              🔒 Locked
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {module?.name || INTENSIVE_MODULE_NAMES[moduleNumber - 1]}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Completion indicators */}
                      {status.bothCompleted && (
                        <span className="flex items-center gap-1 text-emerald-600 text-sm font-medium">
                          <CheckCircle className="w-4 h-4" />
                          Both Complete
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-4 bg-gray-50">
                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 pt-2">
                        {/* Lock/Unlock */}
                        {status.canTake ? (
                          <button
                            onClick={() => handleLockModule(moduleNumber)}
                            disabled={processing}
                            className="flex items-center gap-2 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition text-sm font-medium disabled:opacity-50"
                          >
                            <Lock className="w-4 h-4" />
                            Lock Module
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnlockModule(moduleNumber)}
                            disabled={processing}
                            className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-sm font-medium disabled:opacity-50"
                          >
                            <Unlock className="w-4 h-4" />
                            Unlock Module
                          </button>
                        )}

                        {/* Reveal/Hide Results */}
                        {status.bothCompleted && (
                          status.resultsRevealed ? (
                            <button
                              onClick={() => handleHideResults(moduleNumber)}
                              disabled={processing}
                              className="flex items-center gap-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-medium disabled:opacity-50"
                            >
                              <EyeOff className="w-4 h-4" />
                              Hide Results
                            </button>
                          ) : (
                            <button
                              onClick={() => handleRevealResults(moduleNumber)}
                              disabled={processing}
                              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium disabled:opacity-50"
                            >
                              <Eye className="w-4 h-4" />
                              Reveal Results
                            </button>
                          )
                        )}

                        {/* Preview Quiz Questions */}
                        <button
                          onClick={() => {
                            setSelectedModule(moduleNumber);
                            setShowQuizPreview(true);
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition text-sm font-medium"
                        >
                          <FileQuestion className="w-4 h-4" />
                          Preview Quiz
                        </button>

                        {/* View Teaching */}
                        <button
                          onClick={() => {
                            setSelectedModule(moduleNumber);
                            setShowTeaching(true);
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition text-sm font-medium"
                        >
                          <BookOpen className="w-4 h-4" />
                          Teaching
                        </button>

                        {/* Deep Prayer Button */}
                        <button
                          onClick={() => handleOpenPrayer(moduleNumber, 'long')}
                          className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium"
                        >
                          <Heart className="w-4 h-4" />
                          Deep Prayer
                        </button>

                        {/* Quick Prayer Button */}
                        <button
                          onClick={() => handleOpenPrayer(moduleNumber, 'short')}
                          className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition text-sm font-medium"
                        >
                          <Zap className="w-4 h-4" />
                          Quick Prayer
                        </button>
                      </div>

                      {/* Partner Progress Details */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Partner 1 */}
                        <div className={`p-4 rounded-lg border ${
                          status.partner1Completed 
                            ? 'bg-emerald-50 border-emerald-200' 
                            : 'bg-white border-gray-200'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-gray-500" />
                            <span className="font-medium text-gray-900">{couple?.partner1Name}</span>
                          </div>
                          {status.partner1Completed ? (
                            <div>
                              <div className="flex items-center gap-2 text-emerald-700">
                                <CheckCircle className="w-4 h-4" />
                                <span className="text-sm font-medium">Completed</span>
                              </div>
                              {status.partner1Score !== undefined && (
                                <div className="mt-2">
                                  <span className="text-2xl font-bold text-emerald-600">
                                    {status.partner1Score}
                                  </span>
                                  <span className="text-gray-500">/{status.maxScore}</span>
                                  {getScoreLevel(status.partner1Score, moduleNumber) && (
                                    <span className={`ml-2 text-sm ${
                                      getScoreLevel(status.partner1Score, moduleNumber).color === 'green' 
                                        ? 'text-emerald-600' 
                                        : getScoreLevel(status.partner1Score, moduleNumber).color === 'yellow'
                                        ? 'text-amber-600'
                                        : 'text-red-600'
                                    }`}>
                                      {getScoreLevel(status.partner1Score, moduleNumber).icon} {getScoreLevel(status.partner1Score, moduleNumber).level}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-gray-500">
                              <Clock className="w-4 h-4" />
                              <span className="text-sm">Not completed</span>
                            </div>
                          )}
                        </div>

                        {/* Partner 2 */}
                        <div className={`p-4 rounded-lg border ${
                          status.partner2Completed 
                            ? 'bg-emerald-50 border-emerald-200' 
                            : 'bg-white border-gray-200'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-gray-500" />
                            <span className="font-medium text-gray-900">{couple?.partner2Name}</span>
                          </div>
                          {status.partner2Completed ? (
                            <div>
                              <div className="flex items-center gap-2 text-emerald-700">
                                <CheckCircle className="w-4 h-4" />
                                <span className="text-sm font-medium">Completed</span>
                              </div>
                              {status.partner2Score !== undefined && (
                                <div className="mt-2">
                                  <span className="text-2xl font-bold text-emerald-600">
                                    {status.partner2Score}
                                  </span>
                                  <span className="text-gray-500">/{status.maxScore}</span>
                                  {getScoreLevel(status.partner2Score, moduleNumber) && (
                                    <span className={`ml-2 text-sm ${
                                      getScoreLevel(status.partner2Score, moduleNumber).color === 'green' 
                                        ? 'text-emerald-600' 
                                        : getScoreLevel(status.partner2Score, moduleNumber).color === 'yellow'
                                        ? 'text-amber-600'
                                        : 'text-red-600'
                                    }`}>
                                      {getScoreLevel(status.partner2Score, moduleNumber).icon} {getScoreLevel(status.partner2Score, moduleNumber).level}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-gray-500">
                              <Clock className="w-4 h-4" />
                              <span className="text-sm">Not completed</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Results Revealed Status */}
                      {status.bothCompleted && (
                        <div className={`p-3 rounded-lg flex items-center gap-2 ${
                          status.resultsRevealed 
                            ? 'bg-blue-50 border border-blue-200' 
                            : 'bg-gray-100 border border-gray-200'
                        }`}>
                          {status.resultsRevealed ? (
                            <>
                              <Eye className="w-4 h-4 text-blue-600" />
                              <span className="text-sm text-blue-700 font-medium">
                                Results are visible to the couple
                              </span>
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-600">
                                Results are hidden from the couple
                              </span>
                            </>
                          )}
                        </div>
                      )}

                      {/* Warning if not completed */}
                      {!status.bothCompleted && status.canTake && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                          <span className="text-sm text-amber-700">
                            Waiting for both partners to complete this module before results can be revealed.
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default CounselorIntensiveControl;
